import { ListItem, ListItemButton, ListItemText } from '@mui/material';
import { CuriosityNamespaceType, CuriosityTranslation, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { usePermissions, Restricted } from '@procureai/permissions-ui';
import { identity } from 'ramda';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Optional } from 'ts-toolbelt/out/Object/Optional';
import { Nullable } from 'ts-toolbelt/out/Union/Nullable';

import { dimToKeyMap } from '../../SourcingProjectSupplierWidget.hooks';
import { I18nNamespace } from '~/common/types';
import { RouteId } from '~/routes';

import { TruncatedTypographySpan } from './SupplierListItem.styled';

export const SOURCING_PROJECT_SUPPLIER_LIST_ITEM_HEIGHT = 92;

type DimToKeyMapValues = (typeof dimToKeyMap)[keyof typeof dimToKeyMap];

// using types here to explicitly outline optional properties in resulting type
type SupplierListItemProps = Optional<Record<DimToKeyMapValues, Nullable<string | number>>> & {
  supplierId: string;
  proposalCount?: number;
};

type SupplierListItemCoreProps = SupplierListItemProps & {
  curiosityTranslation: CuriosityTranslation;
};

const SupplierListItemContent = ({
  curiosityTranslation,
  supplierId,
  locationId,
  proposalCount = 0,
}: SupplierListItemCoreProps) => {
  const { t } = useTranslation(I18nNamespace.base, {
    keyPrefix: 'SourcingProjectSupplierWidget.content',
  });
  const navigate = useNavigate();

  const item = (
    <ListItemText
      sx={{ margin: 0, paddingY: 1 }}
      primary={<TruncatedTypographySpan component={'span'}>{curiosityTranslation.value}</TruncatedTypographySpan>}
      secondary={
        <>
          {locationId ? (
            <TruncatedTypographySpan>
              <CuriosityValue ns={CuriosityNamespaceType.SUPPLIER_LOCATION} i18nKey={`${locationId}`} />
            </TruncatedTypographySpan>
          ) : null}
          <TruncatedTypographySpan>{t('proposals', { count: proposalCount ?? 0 })}</TruncatedTypographySpan>
        </>
      }
      slotProps={{
        primary: {
          display: 'flex',
        },
        secondary: {
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    />
  );

  return (
    <Restricted needs={{ permission: Permission.BaseSupplierList }} fallback={item}>
      <ListItemButton //
        onClick={() => navigate(RouteId.supplierDetail(supplierId))}
      >
        {item}
      </ListItemButton>
    </Restricted>
  );
};

export const SupplierListItem = (props: SupplierListItemProps) => {
  const { supplierId } = props;
  const { hasPermission } = usePermissions();
  return (
    <ListItem disablePadding={hasPermission(Permission.BaseSupplierList)}>
      <CuriosityValue ns={CuriosityNamespaceType.SUPPLIER} i18nKey={supplierId} valueGetter={identity}>
        {(value) => <SupplierListItemContent curiosityTranslation={value as CuriosityTranslation} {...props} />}
      </CuriosityValue>
    </ListItem>
  );
};
